import { useState, useEffect, useRef, lazy, Suspense, memo, useCallback, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Search, Instagram, ArrowRight, ChevronDown, Heart } from "lucide-react";
import { useSaleDetection } from "@/hooks/useSaleDetection";

// Lazy-load heavy sub-components that aren't needed on first render
const SearchOverlay = lazy(() => import("@/components/SearchOverlay"));
const CartDrawer = lazy(() => import("@/components/CartDrawer"));

const bagSubCollections = [
  { label: "All Bags", href: "/bags" },
  { label: "Bestsellers", href: "/bags?collection=bestsellers" },
  { label: "Classics", href: "/bags?collection=classics" },
  { label: "Mini Bags", href: "/bags?collection=mini" },
  { label: "Crossbody", href: "/bags?collection=crossbody" },
  { label: "Big Bags", href: "/bags?collection=big" },
  { label: "Denim", href: "/bags?collection=denim" },
];

const navLinks = [
  { label: "New Arrivals", href: "/shop?collection=spring-2026", isNew: true },
  { label: "Our Bags", href: "/bags", hasDropdown: true },
  { label: "Clothing", href: "/clothing" },
  { label: "Accessories", href: "/accessories" },
  { label: "Atelier", href: "/atelier" },
  { label: "Workshops", href: "/workshops" },
  
  { label: "About", href: "/about" },
];

const mobileExtraLinks = [
  { label: "Shop All", href: "/shop" },
  { label: "Wishlist", href: "/wishlist" },
  { label: "Journal", href: "/blog" },
  { label: "FAQ & Help", href: "/faq" },
  { label: "Contact", href: "/contact" },
];

// Route chunk map for prefetching on hover
const routeChunkMap: Record<string, () => Promise<unknown>> = {
  "/sale": () => import("@/pages/Sale"),
  "/shop": () => import("@/pages/Shop"),
  "/bags": () => import("@/pages/Bags"),
  "/clothing": () => import("@/pages/Clothing"),
  "/accessories": () => import("@/pages/Accessories"),
  "/atelier": () => import("@/pages/Atelier"),
  "/workshops": () => import("@/pages/Workshops"),
  "/about": () => import("@/pages/About"),
  "/wishlist": () => import("@/pages/Wishlist"),
  "/blog": () => import("@/pages/Blog"),
  "/faq": () => import("@/pages/FAQ"),
  "/contact": () => import("@/pages/Contact"),
};

const prefetchedRoutes = new Set<string>();

const prefetchRoute = (href: string) => {
  // Strip query params for matching
  const path = href.split("?")[0];
  if (prefetchedRoutes.has(path)) return;
  const loader = routeChunkMap[path];
  if (loader) {
    prefetchedRoutes.add(path);
    loader();
  }
};

const Navbar = memo(() => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [bagsDropdownOpen, setBagsDropdownOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [cartLoaded, setCartLoaded] = useState(false);
  const dropdownRef = useRef<HTMLLIElement>(null);
  const navRef = useRef<HTMLElement>(null);
  const location = useLocation();
  const { hasSale } = useSaleDetection();

  // Filter out Sale link when no sale products exist
  const filteredNavLinks = navLinks;

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setBagsDropdownOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setBagsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  // Eagerly load CartDrawer after initial render (it's needed for the icon/badge)
  useEffect(() => {
    const timer = setTimeout(() => setCartLoaded(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const handlePrefetch = useCallback((href: string) => () => prefetchRoute(href), []);

  return (
    <>
      {/* Announcement bars */}
      <div className="bg-foreground py-2 text-center">
        <Link
          to="/shop?collection=spring-2026"
          className="font-body text-xs tracking-[0.2em] uppercase text-primary-foreground/80 hover:text-primary-foreground transition-colors"
          onMouseEnter={handlePrefetch("/shop")}
        >
          New Arrivals are here — Clouds, Stripes & Organza →
        </Link>
      </div>
      <div className="bg-secondary py-1.5 text-center border-b border-border">
        <p className="font-body text-[10px] tracking-[0.15em] uppercase text-muted-foreground">
          Handmade in Amsterdam · Free shipping over €180 within EU · Made to order
        </p>
      </div>

      <header
        ref={navRef}
        className={`sticky top-0 z-[60] transition-all duration-300 ${
          scrolled
            ? "bg-background backdrop-blur-lg shadow-[0_1px_12px_hsl(var(--foreground)/0.08)] border-b border-border/50"
            : "bg-background border-b border-border"
        }`}
      >
        <nav
          className={`container flex items-center justify-between px-6 md:px-8 transition-all duration-300 ${
            scrolled ? "h-14" : "h-16"
          }`}
        >
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden text-foreground relative z-[60]"
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
          </button>

          <Link
            to="/"
            className={`font-display font-bold tracking-tight text-foreground transition-all duration-300 absolute left-1/2 -translate-x-1/2 md:static md:translate-x-0 ${
              scrolled ? "text-xl" : "text-2xl"
            }`}
          >
            NOTT
          </Link>

          <ul className="hidden md:flex items-center gap-8">
            {filteredNavLinks.map((link) => {
              const isActive = location.pathname === link.href;
              
              if (link.hasDropdown) {
                return (
                  <li
                    key={link.label}
                    ref={dropdownRef}
                    className="relative"
                    onMouseEnter={() => {
                      setBagsDropdownOpen(true);
                      prefetchRoute(link.href);
                    }}
                    onMouseLeave={() => setBagsDropdownOpen(false)}
                  >
                    <button
                      className={`font-body text-sm tracking-wide transition-colors relative inline-flex items-center gap-1 ${
                        isActive || location.pathname === "/bags"
                          ? "text-foreground"
                          : "text-muted-foreground hover:text-foreground"
                      }`}
                      onClick={() => setBagsDropdownOpen(!bagsDropdownOpen)}
                    >
                      {link.label}
                      <ChevronDown size={12} className={`transition-transform duration-200 ${bagsDropdownOpen ? "rotate-180" : ""}`} />
                      {(isActive || location.pathname === "/bags") && (
                        <motion.span
                          layoutId="nav-underline"
                          className="absolute -bottom-1 left-0 right-0 h-px bg-foreground"
                          transition={{ type: "spring", bounce: 0.2, duration: 0.4 }}
                        />
                      )}
                    </button>

                    <AnimatePresence>
                      {bagsDropdownOpen && (
                        <motion.div
                          initial={{ opacity: 0, y: 4 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: 4 }}
                          transition={{ duration: 0.15 }}
                          className="absolute top-full left-0 mt-2 bg-background border border-border shadow-lg min-w-[180px] py-2"
                        >
                          {bagSubCollections.map((sub) => (
                            <Link
                              key={sub.label}
                              to={sub.href}
                              className="block px-4 py-2 font-body text-sm text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
                              onMouseEnter={handlePrefetch(sub.href)}
                            >
                              {sub.label}
                            </Link>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </li>
                );
              }

              return (
                <li key={link.label}>
                  <Link
                    to={link.href}
                    onMouseEnter={handlePrefetch(link.href)}
                    className={`font-body text-sm tracking-wide transition-colors relative ${
                      (link as any).isSale
                        ? "text-destructive hover:text-destructive/80 font-medium"
                        : isActive
                          ? "text-foreground"
                          : "text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    {link.label}
                    {(link as any).isNew && (
                      <span className="ml-1 inline-block w-1.5 h-1.5 rounded-full bg-accent align-super" />
                    )}
                    {isActive && (
                      <motion.span
                        layoutId="nav-underline"
                        className="absolute -bottom-1 left-0 right-0 h-px bg-foreground"
                        transition={{ type: "spring", bounce: 0.2, duration: 0.4 }}
                      />
                    )}
                  </Link>
                </li>
              );
            })}
          </ul>

          <div className="flex items-center gap-3">
            <button
              aria-label="Search"
              onClick={() => setSearchOpen(true)}
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              <Search size={18} />
            </button>
            <Link
              to="/wishlist"
              aria-label="Wishlist"
              className="text-muted-foreground hover:text-foreground transition-colors"
              onMouseEnter={handlePrefetch("/wishlist")}
            >
              <Heart size={18} />
            </Link>
            {/* Cart drawer — lazy loaded after initial render */}
            {cartLoaded && (
              <Suspense fallback={<div className="w-[18px] h-[18px]" />}>
                <CartDrawer />
              </Suspense>
            )}
          </div>
        </nav>
      </header>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
              className="md:hidden fixed inset-0 bg-foreground/20 backdrop-blur-sm z-[65]"
              onClick={() => setMobileOpen(false)}
            />
            {/* Menu panel */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.25 }}
              className={`md:hidden fixed inset-y-0 left-0 right-12 bg-background z-[66] overflow-y-auto shadow-elevated ${
                scrolled ? "pt-14" : "pt-16"
              }`}
            >
              <div className="flex flex-col justify-between min-h-[calc(100vh-4rem)] p-8">
                {/* Close button */}
                <button
                  onClick={() => setMobileOpen(false)}
                  className="absolute top-4 right-4 text-foreground z-10"
                  aria-label="Close menu"
                >
                  <X size={22} />
                </button>
                <div>
                <ul className="flex flex-col gap-1">
           {filteredNavLinks.map((link, i) => (
                    <motion.li
                      key={link.label}
                      initial={{ opacity: 0, x: -16 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.3, delay: i * 0.06 }}
                    >
                      <Link
                        to={link.href}
                        className="group flex items-center justify-between py-4 border-b border-border"
                        onClick={() => setMobileOpen(false)}
                      >
                        <span className="font-display text-2xl font-semibold text-foreground group-hover:text-muted-foreground transition-colors">
                          {link.label}
                          {(link as any).isNew && (
                            <span className="ml-2 inline-block w-2 h-2 rounded-full bg-accent" />
                          )}
                        </span>
                        <ArrowRight size={18} className="text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                      </Link>
                    </motion.li>
                  ))}
                </ul>

                <div className="mt-6 pl-4 border-l-2 border-border">
                  <p className="font-body text-[10px] tracking-[0.2em] uppercase text-muted-foreground mb-3">Bag Collections</p>
                  {bagSubCollections.slice(1).map((sub) => (
                    <Link
                      key={sub.label}
                      to={sub.href}
                      className="block py-1.5 font-body text-sm text-muted-foreground hover:text-foreground transition-colors"
                      onClick={() => setMobileOpen(false)}
                    >
                      {sub.label}
                    </Link>
                  ))}
                </div>

                <ul className="mt-8 flex flex-col gap-3">
                  {mobileExtraLinks.map((link, i) => (
                    <motion.li
                      key={link.label}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.3, delay: 0.4 + i * 0.06 }}
                    >
                      <Link
                        to={link.href}
                        className="font-body text-sm text-muted-foreground hover:text-foreground transition-colors"
                        onClick={() => setMobileOpen(false)}
                      >
                        {link.label}
                      </Link>
                    </motion.li>
                  ))}
                </ul>
              </div>

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.4, delay: 0.5 }}
                className="pt-8 border-t border-border"
              >
                <a
                  href="https://www.instagram.com/nott_bags/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
                >
                  <Instagram size={18} />
                  <span className="font-body text-sm">@nott_bags</span>
                </a>
              </motion.div>
            </div>
          </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Search overlay — only loaded when opened */}
      {searchOpen && (
        <Suspense fallback={null}>
          <SearchOverlay open={searchOpen} onClose={() => setSearchOpen(false)} />
        </Suspense>
      )}
    </>
  );
});

Navbar.displayName = "Navbar";

export default Navbar;
